import React from 'react';
import { Link } from 'react-router-dom';
import { DATA_FINAL_QUIZ_DISABLE } from '@hooks/useFinalQuizAbortGuard.ts';
import type { SidebarNavigationItem } from '@hooks/useSidebarNavigation.tsx';
import { useSidebarNavigation } from '@hooks/useSidebarNavigation.tsx';
import { useUserRoles } from '@hooks/useUserRoles.ts';
import { Menu } from '@layouts/main/sider/menu/styled.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';

interface Props {
    position?: 'top' | 'bottom';
    sidebarKeys?: string[];
    collapsed: boolean;
    selectedKey: string;
    onSelect: (key: string) => void;
}

const SiderMenu: React.FC<Props> = ({ position = 'top', sidebarKeys = [], collapsed, selectedKey, onSelect }) => {
    const { t } = useLingui();
    const userRoles = useUserRoles();
    const sidebarNavigation = useSidebarNavigation(userRoles);

    const MAPPED_TITLES: Record<string, string> = {
        settings: t`Settings`,
        learning: t`Learning`,
        assessment: t`Assessment`,
        progress: t`Progress`,
        survey: t`Survey`,
        elements: t`Elements`,
        pathways: t`Pathways`,
        admin: t`Admin`,
        ask: t`Ask`,
        perform: t`Perform`,
        ['help-center']: t`Help`, // TODO: revert back to Help Center after demo
    };

    const checkPrivilegesRoles = (navigation: SidebarNavigationItem[], roles: string[]) => {
        const current = roles.map((r) => r.toLowerCase());

        return navigation.filter((nav) => {
            const required = (nav.role ?? []).map((r) => r.toLowerCase());
            if (required.length === 0) return true; // no role required

            return required.some((req) => current.some((currentRole) => currentRole === req));
        });
    };

    const navWithAccess = checkPrivilegesRoles(sidebarNavigation, userRoles);
    const orderedNavItems = sidebarKeys
        .map((key) => navWithAccess.find((item) => item.key === key))
        .filter(Boolean) as SidebarNavigationItem[];

    return (
        <Menu
            mode="inline"
            $position={position}
            $collapsed={collapsed}
            selectedKeys={selectedKey ? [selectedKey] : []}
            onSelect={({ key }) => onSelect?.(key)}
            items={orderedNavItems.map((nav) => {
                return {
                    key: nav.key,
                    disabled: nav.disabled,
                    label: (
                        <Link
                            to={nav.url || ''}
                            state={nav.key === ROUTES.SIDEBAR.LEARNING ? { resetLearningTab: true } : undefined}
                            {...DATA_FINAL_QUIZ_DISABLE}
                        >
                            {MAPPED_TITLES[nav.key] ?? nav.title}
                        </Link>
                    ),
                    icon: nav.icon,
                };
            })}
        />
    );
};

export default SiderMenu;
